import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, AuthUser } from '../../services/auth.service';
import { RecruitmentApiService } from '../../services/recruitment-api.service';
import { OrganizationLinkDetails } from '../models/recruitment.models';

@Component({
  selector: 'app-join-organization',
  templateUrl: './join-organization.component.html',
  styleUrls: ['./join-organization.component.css'],
  standalone: false,
})
export class JoinOrganizationComponent implements OnInit {
  token = '';
  details: OrganizationLinkDetails | null = null;
  currentUser: AuthUser | null = null;
  loading = true;
  claiming = false;
  joined = false;
  showRegistration = false;
  registering = false;
  registrationStep: 'form' | 'verify' | 'confirmed' = 'form';
  verificationCode = '';
  registration = { firstName: '', lastName: '', email: '', password: '', confirmPassword: '' };
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private recruitmentApi: RecruitmentApiService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.currentUser = this.authService.getCurrentUser();
    if (!this.token) {
      this.loading = false;
      this.errorMessage = 'This organization link is invalid.';
      return;
    }
    sessionStorage.setItem('konvac_pending_org_token', this.token);
    this.recruitmentApi.getOrganizationLinkDetails(this.token).subscribe({
      next: (details) => {
        this.details = details;
        this.loading = false;
        if (details.revoked) this.errorMessage = 'This organization link has been revoked.';
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'This organization link is invalid or has expired.';
      },
    });
  }

  signIn(): void {
    this.router.navigate(['/login'], { queryParams: { returnUrl: `/join/${encodeURIComponent(this.token)}` } });
  }

  openRegistration(): void {
    this.errorMessage = '';
    this.showRegistration = true;
  }

  cancelRegistration(): void {
    this.errorMessage = '';
    this.showRegistration = false;
  }

  get registrationValid(): boolean {
    return !!this.registration.firstName.trim()
      && !!this.registration.lastName.trim()
      && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.registration.email.trim())
      && this.registration.password.length >= 8
      && this.registration.password === this.registration.confirmPassword;
  }

  async register(): Promise<void> {
    if (!this.registrationValid || this.registering) return;
    this.registering = true;
    this.errorMessage = '';
    try {
      await this.authService.signUpOrganizationMember(
        this.registration.email.trim().toLowerCase(),
        this.registration.password,
        this.registration.firstName.trim(),
        this.registration.lastName.trim(),
        this.token
      );
      this.registrationStep = 'verify';
    } catch (err: any) {
      this.errorMessage = err?.message || 'We could not create your account. Please try again.';
    } finally {
      this.registering = false;
    }
  }

  async confirmRegistration(): Promise<void> {
    if (!this.verificationCode.trim() || this.registering) return;
    this.registering = true;
    this.errorMessage = '';
    const email = this.registration.email.trim().toLowerCase();
    try {
      try {
        await this.authService.confirmOrganizationMember(email, this.verificationCode.trim(), this.token);
      } catch (err: any) {
        // Confirmation may have completed even if a post-confirmation dependency
        // briefly failed. A confirmed user can safely continue through sign-in.
        if (err?.code !== 'NotAuthorizedException') throw err;
      }
      this.registrationStep = 'confirmed';
      try {
        await this.authService.login(email, this.registration.password);
        this.currentUser = this.authService.getCurrentUser();
        this.showRegistration = false;
        this.claim();
      } catch {
        this.errorMessage = 'Your email is verified. Sign in to finish joining the organization.';
      }
    } catch (err: any) {
      this.errorMessage = err?.message || 'We could not verify your account. Please try again.';
    } finally {
      this.registering = false;
    }
  }

  async resendCode(): Promise<void> {
    if (this.registering) return;
    this.registering = true;
    this.errorMessage = '';
    try {
      await this.authService.resendOrganizationConfirmation(
        this.registration.email.trim().toLowerCase(), this.token
      );
    } catch (err: any) {
      this.errorMessage = err?.message || 'We could not resend the code. Please try again.';
    } finally {
      this.registering = false;
    }
  }

  get canJoinAsCandidate(): boolean {
    return this.currentUser?.role === 'member';
  }

  claim(): void {
    if (!this.currentUser || !this.details || !this.canJoinAsCandidate) return;
    this.claiming = true;
    this.errorMessage = '';
    this.recruitmentApi.claimOrganizationLink(this.token).subscribe({
      next: () => {
        sessionStorage.removeItem('konvac_pending_org_token');
        this.claiming = false;
        this.joined = true;
      },
      error: () => {
        this.claiming = false;
        this.errorMessage = 'We could not add you to this organization. Please try again.';
      },
    });
  }

  continueToKonvac(): void {
    this.router.navigate(['/assesments']);
  }
}
